/**
 * Formatting utilities extracted from AM3Formatter component
 */

/**
 * Get timezone abbreviation using Intl.DateTimeFormat
 * @param date - Date object, string, or timestamp
 * @param timeZone - IANA timezone string (e.g., 'America/New_York', 'Australia/Sydney')
 * @returns Timezone abbreviation (e.g., 'EDT', 'AEDT') or empty string
 */
export function formatTimezone(date: Date | string, timeZone?: string, format: 'short' | 'long' = 'short'): string {
  if (!date || !timeZone) {
    return ''
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) {
      return ''
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      timeZoneName: format,
    })
    const parts = formatter.formatToParts(dateObj)
    const tzPart = parts.find(part => part.type === 'timeZoneName')

    if (!tzPart?.value) return ''

    // If we got a GMT offset, try to get a better abbreviation
    if (tzPart.value.startsWith('GMT')) {
      // Try to get the long format and extract abbreviation
      const longFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        timeZoneName: 'long',
      })
      const longParts = longFormatter.formatToParts(dateObj)
      const longTzPart = longParts.find(part => part.type === 'timeZoneName')

      if (longTzPart?.value && !longTzPart.value.startsWith('GMT')) {
        // Extract initials from long timezone name (e.g., "Australian Eastern Daylight Time" -> "AEDT")
        const words = longTzPart.value.split(' ')
        return words.map(w => w[0]).join('')
      }

      // If still GMT, convert to UTC format
      return tzPart.value.replace('GMT', 'UTC')
    }

    return tzPart.value
  }
  catch (error) {
    console.error('Error getting timezone abbreviation:', error)
    return ''
  }
}

interface TimeZoneSource {
  startTimeZone?: string | null
  endTimeZone?: string | null
  timeZone?: string | null
}

/**
 * Resolves which timezone to display for an event or event group.
 *
 * A single Event only has `timeZone`. An Event Group has `startTimeZone` /
 * `endTimeZone` instead (the zone of its earliest-starting / latest-ending
 * event) - but some API responses only populate the legacy `timeZone` field.
 * These helpers pick the right field for whichever shape they're given, so
 * callers don't need their own fallback per component.
 */
export function resolveStartTimeZone(source: TimeZoneSource): string | undefined {
  return source.startTimeZone || source.timeZone || undefined
}

export function resolveEndTimeZone(source: TimeZoneSource): string | undefined {
  return source.endTimeZone || source.timeZone || undefined
}

export interface FormatOptions {
  code?: string
  decimal_digits?: number
  decimals?: number
  dateStyle?: 'full' | 'long' | 'medium' | 'short' | 'custom-date'
  timeStyle?: 'full' | 'long' | 'medium' | 'short' | 'time' | 'time-with-zone'
  numeric?: 'auto' | 'always'
  timeZone?: string
}

export function formatCurrency(value: number, options: FormatOptions = {}): string {
  // Divide by 100 to convert cents to dollars, matching behavior in FanTable.vue
  // This is necessary because monetary values are stored in cents in the database
  const valueInDollars = value / 100
  const decimalDigits = options.decimal_digits ?? 2
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: options.code || 'USD',
    // Intl defaults to the "international" symbol — A$, NZ$, CA$, HK$ — while
    // getCurrencySymbol returns symbol_native ('$' for all of those). Without
    // this the two paths disagree on the same page for AU/NZ/CA/HK promoters.
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: decimalDigits,
    maximumFractionDigits: decimalDigits,
  })
  return formatter.format(valueInDollars)
}

export function formatNumber(value: number, options: FormatOptions = {}): string {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: options.decimals || 0,
    maximumFractionDigits: options.decimals || 0,
  })
  return formatter.format(value)
}

/**
 * Abbreviate a number for a chart axis label: 1500 -> "1.5k", 2000000 -> "2m".
 * A whole multiple drops the decimal, so 2000 is "2k" rather than "2.0k".
 *
 * `prefix` is prepended to every branch — pass the currency symbol for a money
 * axis and nothing for a count. It exists so the symbol cannot be applied to
 * some magnitudes and missed on others, which is what happened while three
 * chart components each carried their own copy of this.
 *
 * @param value - The number to abbreviate
 * @param prefix - Prepended to the result (e.g. a currency symbol)
 * @returns The abbreviated label (e.g. "£1.5k")
 */
export function abbreviateNumber(value: number, prefix: string = ''): string {
  if (value >= 1000000) {
    return `${prefix}${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}m`
  }
  if (value >= 1000) {
    return `${prefix}${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`
  }
  return `${prefix}${Math.round(value)}`
}

/**
 * Format a currency value (already in dollars) with commas and 2 decimal places
 * @param value - The value in dollars (not cents)
 * @param decimalDigits - Number of decimal places (default: 2)
 * @returns Formatted value with commas (e.g., "44,063.60")
 */
export function formatCurrencyValueWithCommas(value: number, decimalDigits: number = 2): string {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalDigits,
    maximumFractionDigits: decimalDigits,
  })
  return formatter.format(value)
}

export function formatDateTime(value: string | number | null | Date, options: FormatOptions = {}): string | null {
  if (!value || value === null) {
    return null
  }

  try {
    let date: Date

    // Handle different input types
    if (value instanceof Date) {
      date = value
    }
    else if (typeof value === 'number') {
      // Unix timestamp - convert to milliseconds
      date = new Date(value * 1000)
    }
    else if (typeof value === 'string') {
      // Check if it's a numeric string (Unix timestamp)
      const numericValue = Number(value)
      if (!isNaN(numericValue) && value.length <= 10) {
        // Likely a Unix timestamp (10 digits or less)
        date = new Date(numericValue * 1000)
      }
      else {
        // Regular date string
        // If the string doesn't have a timezone indicator (Z or +/- offset), treat it as UTC
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value)) {
          // Add 'Z' to indicate UTC
          date = new Date(value + 'Z')
        }
        else {
          date = new Date(value)
        }
      }
    }
    else {
      console.error('invalid date format:', value, 'Error: Invalid date input type')
      return null
    }

    // Validate the date
    if (isNaN(date.getTime())) {
      console.error('invalid date format:', value, 'Error: Invalid date')
      return null
    }

    // Handle special case for timeStyle "time" (8:30pm format)
    if (options.timeStyle === 'time') {
      if (options.timeZone) {
        // Use timezone-aware formatting
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: options.timeZone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        const formatted = formatter.format(date)
        // Convert "8:30 PM" to "8:30pm" format
        return formatted.replace(/\s?(AM|PM)/, match => match.trim().toLowerCase())
      }
      else {
        // Use local timezone (original behavior)
        const hours = date.getHours() % 12 || 12 // Convert to 12-hour format
        const minutes = date.getMinutes().toString().padStart(2, '0')
        const ampm = date.getHours() >= 12 ? 'pm' : 'am'
        return `${hours}:${minutes}${ampm}`
      }
    }

    // Handle special case for timeStyle "time-with-zone"
    if (options.timeStyle === 'time-with-zone') {
      // Format the time part
      const timeFormatter = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: options.timeZone,
      })
      const time = timeFormatter.format(date)

      // Get timezone abbreviation using our helper
      const tz = formatTimezone(date, options.timeZone)

      return tz ? `${time} ${tz}` : time
    }

    // For custom date-only formatting, use MMM D, YYYY format
    if (options.dateStyle === 'custom-date') {
      if (options.timeZone) {
        // Use timezone-aware formatting
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: options.timeZone,
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
        return formatter.format(date)
      }
      else {
        // Use local timezone (original behavior)
        const monthNames = [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
        ]
        const month = monthNames[date.getMonth()]
        const day = date.getDate()
        const year = date.getFullYear()
        return `${month} ${day}, ${year}`
      }
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      dateStyle: options.dateStyle || 'medium',
      timeStyle: options.timeStyle,
    })
    return formatter.format(date)
  }
  catch (error) {
    console.error('invalid date format:', value, error)
    return null
  }
}

/**
 * Formats a date string to a human-readable format (e.g., "Jan 15, 2025")
 * Used in calendar items to display dates in a friendly format
 * Handles timezone-safe date parsing for YYYY-MM-DD format strings
 *
 * @param dateString - ISO date or datetime string
 * @param timeZone - Optional IANA timezone (e.g., "Australia/Sydney", "America/New_York")
 * @returns Formatted date string in "MMM D, YYYY" format
 *
 * @example
 * formatDate('2025-01-15') // Returns: 'Jan 15, 2025'
 * formatDate('2025-01-15T14:30:00Z') // Returns: 'Jan 15, 2025' (in user's timezone)
 * formatDate('2026-01-06T23:00:00.000Z', 'Australia/Sydney') // Returns: 'Jan 7, 2026' (in Sydney timezone)
 */
export function formatDate(dateString: string, timeZone?: string): string {
  try {
    // Check if it's a date-only string (YYYY-MM-DD) to avoid timezone issues
    const dateOnlyMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/)

    if (dateOnlyMatch) {
      // Parse date parts directly to avoid timezone conversion
      const [, year, month, day] = dateOnlyMatch
      const date = new Date(Number(year), Number(month) - 1, Number(day))

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }

    // For datetime strings with time components, use timezone-aware formatting
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timeZone || undefined,
    })
  }
  catch (error) {
    return dateString
  }
}

/**
 * Formats a date range for display, handling both single dates and multi-day ranges
 * Used in calendar items to show date spans
 *
 * @param startDate - ISO date or datetime string for the start date
 * @param endDate - Optional ISO date or datetime string for the end date
 * @param timeZone - Optional IANA timezone for displaying dates in specific timezone
 * @returns Formatted date string, either single date or range
 *
 * @example
 * formatDateRange('2025-01-15') // Returns: 'Jan 15, 2025'
 * formatDateRange('2025-01-15', '2025-01-17') // Returns: 'Jan 15, 2025 - Jan 17, 2025'
 * formatDateRange('2025-01-15', '2025-01-15') // Returns: 'Jan 15, 2025' (same day)
 * formatDateRange('2026-01-06T23:00:00.000Z', undefined, 'Australia/Sydney') // Returns: 'Jan 7, 2026' (Sydney time)
 */
export function formatDateRange(startDate: string, endDate?: string, timeZone?: string): string {
  if (!endDate || startDate === endDate) {
    return formatDate(startDate, timeZone)
  }

  return `${formatDate(startDate, timeZone)} - ${formatDate(endDate, timeZone)}`
}

export function formatRelativeTime(value: string, options: FormatOptions = {}): string {
  // If the string doesn't have a timezone indicator (Z or +/- offset), treat it as UTC
  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value)
  const normalizedValue = value.includes('T') && !hasTimeZone ? `${value}Z` : value
  const date = new Date(normalizedValue)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  const rtf = new Intl.RelativeTimeFormat('en', {
    numeric: options.numeric || 'auto',
  })

  const units = [
    { unit: 'year', ms: 31536000000, threshold: 1.5 },
    { unit: 'month', ms: 2592000000, threshold: 1 },
    { unit: 'week', ms: 604800000, threshold: 1 },
    { unit: 'day', ms: 86400000, threshold: 1 },
    { unit: 'hour', ms: 3600000, threshold: 1 },
    { unit: 'minute', ms: 60000, threshold: 1 },
    { unit: 'second', ms: 1000, threshold: 1 },
  ]

  for (const { unit, ms, threshold } of units) {
    const value = Math.floor(diffMs / ms)
    if (Math.abs(value) >= threshold) {
      const string = rtf.format(-value, unit as Intl.RelativeTimeFormatUnit)
      return string.charAt(0).toUpperCase() + string.slice(1)
    }
  }

  return 'Just now'
}

/**
 * Converts a date/datetime string or Date object to YYYY-MM-DD format
 *
 * @param value - ISO datetime string, Date object, or timestamp
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * toDateString('2025-01-15T14:30:00Z') // Returns: '2025-01-15'
 * toDateString(new Date('2025-01-15')) // Returns: '2025-01-15'
 * toDateString(1705330200000) // Returns: '2025-01-15'
 */
export function toDateString(value: string | number | Date): string {
  if (typeof value === 'string') {
    // If already ISO datetime string, extract date part
    return value.split('T')[0]
  }

  const date = value instanceof Date ? value : new Date(value)
  return date.toISOString().split('T')[0]
}

/**
 * Converts a date/datetime string or Date object to timestamp (milliseconds since epoch)
 *
 * @param value - ISO datetime string, Date object, or timestamp
 * @returns Timestamp in milliseconds
 *
 * @example
 * toTimestamp('2025-01-15T14:30:00Z') // Returns: 1705330200000
 * toTimestamp(new Date('2025-01-15')) // Returns: 1705330200000
 * toTimestamp(1705330200000) // Returns: 1705330200000 (no conversion needed)
 */
export function toTimestamp(value: string | number | Date): number {
  if (typeof value === 'number') {
    return value
  }

  const date = value instanceof Date ? value : new Date(value)
  return date.getTime()
}

export function formatEventDate(value: string | number | null | Date): string | null {
  if (!value || value === null) {
    return null
  }

  try {
    let date: Date

    // Handle different input types
    if (value instanceof Date) {
      date = value
    }
    else if (typeof value === 'number') {
      // Unix timestamp - convert to milliseconds
      date = new Date(value * 1000)
    }
    else if (typeof value === 'string') {
      // Check if it's a numeric string (Unix timestamp)
      const numericValue = Number(value)
      if (!isNaN(numericValue) && value.length <= 10) {
        // Likely a Unix timestamp (10 digits or less)
        date = new Date(numericValue * 1000)
      }
      else {
        // Regular date string
        // If the string doesn't have a timezone indicator (Z or +/- offset), treat it as UTC
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value)) {
          // Add 'Z' to indicate UTC
          date = new Date(value + 'Z')
        }
        else {
          date = new Date(value)
        }
      }
    }
    else {
      throw new Error('Invalid date input type')
    }

    // Validate the date
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date')
    }

    // Get current date at midnight (start of day)
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    // Get event date at midnight (start of day)
    const eventDate = new Date(date)
    eventDate.setHours(0, 0, 0, 0)

    // Calculate difference in days
    const diffMs = eventDate.getTime() - now.getTime()
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return 'Event today'
    }

    const absDays = Math.abs(diffDays)
    const isFuture = diffDays > 0

    let timeValue: number
    let timeUnit: string

    if (absDays < 90) {
      // 0-90 days: use days
      timeValue = absDays
      timeUnit = timeValue === 1 ? 'day' : 'days'
    }
    else if (absDays < 365) {
      // 90-365 days: use months
      timeValue = Math.round(absDays / 30.44) // Average length of 1 month
      timeUnit = timeValue === 1 ? 'month' : 'months'
    }
    else {
      // 365+ days: use years
      timeValue = Math.round(absDays / 365.25) // To account for leap years
      timeUnit = timeValue === 1 ? 'year' : 'years'
    }

    if (isFuture) {
      return `Event in ${timeValue} ${timeUnit}`
    }
    else {
      return `Ended ${timeValue} ${timeUnit} ago`
    }
  }
  catch (error) {
    console.error('invalid date format:', value, error)
    return null
  }
}
